import { useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';

import myShopClient from '@services/nearlive';

// One heartbeat per user per UTC calendar day. Deliberately a calendar-date
// stamp rather than a rolling 24h TTL — a rolling window drifts across
// midnight and silently skips whole days, structurally undercounting DAU.
const storageKey = (userId) => `unify:heartbeat:${userId}`;
const todayUtc = () => new Date().toISOString().slice(0, 10);

const sentToday = (userId) => {
	try {
		return window.localStorage.getItem(storageKey(userId)) === todayUtc();
	} catch {
		return false; // private mode / disabled storage → just send; the server dedupes
	}
};

const markSent = (userId) => {
	try {
		window.localStorage.setItem(storageKey(userId), todayUtc());
	} catch {
		// A failed write just means we may send again on the next mount — the
		// (user_id, date) unique index dedupes server-side.
	}
};

/**
 * Fire-and-forget MAU/DAU/WAU heartbeat (NEA-6211): once per Clerk user per UTC
 * calendar day, POST `/api/telemetry/session-heartbeat` so dashboard activity
 * lands in insights-db's dashboard_sessions table for NEA-6210's reporting.
 *
 * Skipped for impersonated HQ sessions (the endpoint also rejects those, along
 * with @near.st logins and non-production stages). Best-effort: failures are
 * swallowed and the stamp is written only on success, so a transient failure
 * retries on the next mount.
 */
export function useSessionHeartbeat() {
	const { isLoaded, isSignedIn, userId, sessionClaims } = useAuth();
	const firedRef = useRef(false);
	const isImpersonating = Boolean(sessionClaims?.act);

	useEffect(() => {
		if (!isLoaded || !isSignedIn || !userId || isImpersonating) return;
		if (firedRef.current || sentToday(userId)) return;
		firedRef.current = true;

		(async () => {
			try {
				const client = await myShopClient();
				const { data } = await client.post('/api/telemetry/session-heartbeat');
				// The endpoint never 500s; a failed write comes back as skipped:'error'.
				// Don't stamp the day for it, so a later mount retries.
				if (data?.skipped === 'error') {
					firedRef.current = false;
					return;
				}
				markSent(userId);
			} catch {
				firedRef.current = false; // allow a retry on a later mount
			}
		})();
	}, [isLoaded, isSignedIn, userId, isImpersonating]);
}
